import { useState } from "react";
import { toast } from "sonner";

const FineResponseModal = ({ fine, employeeData, onClose, onSave }) => {
  const [step, setStep] = useState(1); // 1: consent, 2: details, 3: payment
  const [formData, setFormData] = useState({
    data_consent_accepted: false,
    nif: employeeData?.nif || '',
    address: '',
    phone: employeeData?.phone || '',
    driving_license_number: employeeData?.driving_license_number || '',
    driving_license_expiry: employeeData?.driving_license_expiry || '',
    driving_license_categories: employeeData?.driving_license_categories || '',
    driving_license_country: 'Portugal',
    payment_choice: '',
    payment_notes: '',
    contestation: false,
    contestation_reason: ''
  });

  const handleSubmit = () => {
    if (!formData.data_consent_accepted) {
      toast.error('Deve aceitar o formulário de cedência de dados');
      return;
    }
    if (!formData.driving_license_number || !formData.driving_license_expiry) {
      toast.error('Dados da carta de condução são obrigatórios');
      return;
    }
    if (!formData.payment_choice) {
      toast.error('Deve indicar a forma de pagamento');
      return;
    }
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-slate-700">
          <h3 className="text-xl font-bold text-white">Resposta à Coima</h3>
          <p className="text-sm text-slate-400 mt-1">
            {fine.license_plate} - {fine.infraction_type} - {fine.amount?.toFixed(2)} €
          </p>
          <div className="flex gap-2 mt-3">
            <span className={`px-2 py-1 rounded text-xs ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-400'}`}>1. Consentimento</span>
            <span className={`px-2 py-1 rounded text-xs ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-400'}`}>2. Dados</span>
            <span className={`px-2 py-1 rounded text-xs ${step >= 3 ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-400'}`}>3. Pagamento</span>
          </div>
        </div>
        
        <div className="p-6 space-y-4">
          {/* Step 1: Data Consent */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="p-4 bg-amber-900/30 border border-amber-700 rounded-lg">
                <h4 className="font-bold text-amber-300 mb-2">Formulário de Cedência de Dados Pessoais</h4>
                <div className="text-sm text-slate-300 space-y-2">
                  <p>Para processamento desta coima, é necessário que autorize a cedência dos seguintes dados pessoais:</p>
                  <ul className="list-disc list-inside ml-2 text-slate-400">
                    <li>Nome completo e NIF</li>
                    <li>Morada</li>
                    <li>Contacto telefónico</li>
                    <li>Número e validade da carta de condução</li>
                  </ul>
                  <p className="mt-3">Estes dados serão utilizados exclusivamente para identificação do condutor junto das autoridades competentes, conforme obrigatório por lei.</p>
                </div>
              </div>
              
              <label className="flex items-start gap-3 p-4 bg-slate-700/50 rounded-lg cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.data_consent_accepted}
                  onChange={e => setFormData({...formData, data_consent_accepted: e.target.checked})}
                  className="mt-1"
                  data-testid="consent-checkbox"
                />
                <span className="text-sm text-white">
                  Declaro que li e aceito os termos de cedência de dados pessoais acima descritos, autorizando a empresa a fornecer os meus dados às autoridades para processamento desta coima.
                </span>
              </label>
            </div>
          )}

          {/* Step 2: Personal & License Data */}
          {step === 2 && (
            <div className="space-y-4">
              <h4 className="font-medium text-white">Dados Pessoais</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">NIF *</label>
                  <input
                    type="text"
                    value={formData.nif}
                    onChange={e => setFormData({...formData, nif: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white"
                    placeholder="123456789"
                    data-testid="nif-input"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Telemóvel</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={e => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white"
                    placeholder="912345678"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Morada Completa *</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={e => setFormData({...formData, address: e.target.value})}
                  className="w-full px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white"
                  placeholder="Rua, Número, Código Postal, Localidade"
                  data-testid="address-input"
                  required
                />
              </div>
              
              <h4 className="font-medium text-white pt-4">Carta de Condução</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Número da Carta *</label>
                  <input
                    type="text"
                    value={formData.driving_license_number}
                    onChange={e => setFormData({...formData, driving_license_number: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white"
                    placeholder="L-1234567"
                    data-testid="license-number-input"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Data de Validade *</label>
                  <input
                    type="date"
                    value={formData.driving_license_expiry}
                    onChange={e => setFormData({...formData, driving_license_expiry: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white"
                    data-testid="license-expiry-input"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Categorias</label>
                  <input
                    type="text"
                    value={formData.driving_license_categories}
                    onChange={e => setFormData({...formData, driving_license_categories: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white"
                    placeholder="B, B1, AM"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">País de Emissão</label>
                  <input
                    type="text"
                    value={formData.driving_license_country}
                    onChange={e => setFormData({...formData, driving_license_country: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Payment Choice */}
          {step === 3 && (
            <div className="space-y-4">
              <h4 className="font-medium text-white">Forma de Pagamento</h4>
              <p className="text-sm text-slate-400">Valor da coima: <strong className="text-white">{fine.amount?.toFixed(2)} €</strong></p>
              
              <div className="space-y-3">
                <label className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                  formData.payment_choice === 'employee_pays' 
                    ? 'bg-blue-900/30 border-blue-600' 
                    : 'bg-slate-700/50 border-slate-600 hover:border-slate-500'
                }`}>
                  <input
                    type="radio"
                    name="payment"
                    value="employee_pays"
                    checked={formData.payment_choice === 'employee_pays'}
                    onChange={e => setFormData({...formData, payment_choice: e.target.value})}
                    className="mt-1"
                    data-testid="payment-employee"
                  />
                  <div>
                    <p className="font-medium text-white">Pagamento pelo Colaborador</p>
                    <p className="text-sm text-slate-400">Assumo a responsabilidade e pagarei a coima diretamente</p>
                  </div>
                </label>
                
                <label className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                  formData.payment_choice === 'company_deduct_salary' 
                    ? 'bg-blue-900/30 border-blue-600' 
                    : 'bg-slate-700/50 border-slate-600 hover:border-slate-500'
                }`}>
                  <input
                    type="radio"
                    name="payment"
                    value="company_deduct_salary"
                    checked={formData.payment_choice === 'company_deduct_salary'}
                    onChange={e => setFormData({...formData, payment_choice: e.target.value})}
                    className="mt-1"
                    data-testid="payment-deduct"
                  />
                  <div>
                    <p className="font-medium text-white">Dedução em Salário</p>
                    <p className="text-sm text-slate-400">Autorizo a empresa a deduzir o valor no meu salário do mês corrente/seguinte</p>
                  </div>
                </label>
              </div>

              {formData.payment_choice === 'company_deduct_salary' && (
                <div className="p-3 bg-amber-900/30 border border-amber-700 rounded-lg">
                  <p className="text-sm text-amber-300">
                    Ao selecionar esta opção, autoriza expressamente a dedução de {fine.amount?.toFixed(2)} € no seu próximo processamento salarial.
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Observações</label>
                <textarea
                  value={formData.payment_notes}
                  onChange={e => setFormData({...formData, payment_notes: e.target.value})}
                  className="w-full px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white"
                  rows={2}
                  placeholder="Observações adicionais (opcional)"
                />
              </div>

              {/* Contestation option */}
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.contestation}
                  onChange={e => setFormData({...formData, contestation: e.target.checked})}
                />
                <span className="text-sm text-slate-300">Pretendo contestar esta coima</span>
              </label>
              
              {formData.contestation && (
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Motivo da Contestação</label>
                  <textarea
                    value={formData.contestation_reason}
                    onChange={e => setFormData({...formData, contestation_reason: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg bg-slate-700 border border-slate-600 text-white"
                    rows={3}
                    placeholder="Descreva o motivo da contestação"
                  />
                </div>
              )}
            </div>
          )}
        </div>
        
        <div className="p-6 border-t border-slate-700 flex justify-between">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-slate-700 text-white hover:bg-slate-600">
            Cancelar
          </button>
          <div className="flex gap-2">
            {step > 1 && (
              <button onClick={() => setStep(step - 1)} className="px-4 py-2 rounded-lg bg-slate-600 text-white hover:bg-slate-500">
                Voltar
              </button>
            )}
            {step < 3 ? (
              <button
                onClick={() => setStep(step + 1)}
                disabled={step === 1 && !formData.data_consent_accepted}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-50"
              >
                Próximo
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={!formData.payment_choice}
                className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50"
                data-testid="submit-fine-response"
              >
                Submeter Resposta
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FineResponseModal;
